import React from 'react';
import cn from 'classnames';
import { Button, Dropdown, Menu } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
// Style
import styles from './RowActionsMenu.module.scss';

export type RowMenuAction = {
  /** Stable identity for the row. Also used as the React key. */
  key: string;
  label: React.ReactNode;
  onClick?: () => void;
  /** Renders the action as a link. Use for anything that leaves the page. */
  href?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Red, and placed last in its group. For anything hard to undo. */
  danger?: boolean;
  /**
   * Green. For the affirmative half of a pair the reader scans for by colour -
   * accept against reject, activate against deactivate.
   */
  positive?: boolean;
  /** Starts a new group: draws a rule above this action. */
  startsGroup?: boolean;
  /** Leave the action out entirely, rather than showing it disabled. */
  hidden?: boolean;
};

type RowActionsMenuProps = {
  actions: RowMenuAction[];
  /** Shown above the actions. For a lock holder, or why an action is off. */
  notice?: React.ReactNode;
  /** What the trigger is called for screen readers. */
  label?: string;
  /** Runs when the menu opens. For state the actions need but the row lacks. */
  onOpen?: () => void;
};

/**
 * antd's Menu clones each child and injects its own internal props
 * (eventKey, warnKey). Swallow them here so they never reach the DOM node.
 */
const MenuNotice = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => <div className={styles.notice}>{children}</div>;

/**
 * The actions for one table row, behind a single control.
 *
 * Pass every action the record can take; the menu drops the hidden ones and
 * never draws a leading or doubled divider, so a caller can gate actions on
 * record state without also having to work out the grouping.
 */
const RowActionsMenu = ({
  actions,
  notice,
  label = 'Row actions',
  onOpen,
}: RowActionsMenuProps): JSX.Element | null => {
  const visible = actions.filter((action) => !action.hidden);

  if (!visible.length) {
    return null;
  }

  const overlay = (
    <Menu className={styles.menu} selectable={false}>
      {notice ? <MenuNotice>{notice}</MenuNotice> : null}

      {visible.map((action, index) => (
        <React.Fragment key={action.key}>
          {action.startsGroup && index > 0 && <Menu.Divider />}

          <Menu.Item
            key={action.key}
            className={cn(
              action.positive && !action.disabled && styles.positive
            )}
            icon={action.icon}
            disabled={action.disabled}
            danger={action.danger}
            onClick={action.onClick}
          >
            {action.href ? (
              <a href={action.href} target="_blank" rel="noopener noreferrer">
                {action.label}
              </a>
            ) : (
              action.label
            )}
          </Menu.Item>
        </React.Fragment>
      ))}
    </Menu>
  );

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      overlay={overlay}
      onVisibleChange={(isOpen) => {
        if (isOpen) {
          onOpen?.();
        }
      }}
    >
      <Button
        aria-label={label}
        aria-haspopup="menu"
        icon={<EllipsisOutlined />}
      />
    </Dropdown>
  );
};

export default RowActionsMenu;
